import {getCookie, setPermanentTopLevelCookie} from "../utils";

// Transmit Security Variables
const TRANSMIT_API_CLIENT_ID = process.env.REACT_APP_AUTH_PROVIDER_CLIENT_ID
const AUTH_ENVIRONMENT = process.env.REACT_APP_AUTH_ENVIRONMENT
const TRANSMIT_DEFAULT_REDIRECT_URI = `${window.location.origin}/callback` // The redirect URI is never needed/used by MMJ, but needs to be included in transmit requests.

// Edrington Consumer API details.
const EDRINGTON_API_URL = process.env.REACT_APP_API_URL
const EDRINGTON_API_KEY = process.env.REACT_APP_API_SUBSCRIPTION
export const EDRINGTON_CLIENT_HEADERS = {
  'Ocp-Apim-Subscription-Key': EDRINGTON_API_KEY,
  'cache-control': 'no-cache',
}

/**
 * Fetch the current session data from the authData cookie.
 * AUTH_ENVIRONMENT is used to ensure the cookie is unique for each environment
 */
function getSession() {
  return JSON.parse(getCookie(`authData_${AUTH_ENVIRONMENT}`))
}

/**
 * Save the current session to the authData cookie.
 * AUTH_ENVIRONMENT is used to ensure the cookie is unique for each environment
 */
function setSession(authData) {
  authData.expires_at = Date.now() + 1000 * authData.expires_in
  setPermanentTopLevelCookie(`authData_${AUTH_ENVIRONMENT}`, JSON.stringify(authData))
}

/**
 * Check if the current user is authenticated (has session data and their access token isn't expired)
 */
function isAuthenticated(authData) {
  if (!authData) {
    return false
  }

  // Check whether the current time is past the
  // access token's expiry time
  return Date.now() < authData.expires_at
}

/**
 * Return the current access token for the user or null if one can't be retrieved.
 * This method will automatically attempt to refresh the session if possible too.
 *
 * @returns {Promise<*|null>}
 */
export default async function checkAuth() {
  const authData = getSession()
  if (!authData) {
    return null
  }
  
  if (isAuthenticated(authData)) {
    return authData.access_token
  }
  
  // If the current access token has expired but there is a refresh token, attempt to fetch a new access token.
  if (!isAuthenticated(authData) && authData.refresh_token) {
    const requestBody = new URLSearchParams({
      client_id: TRANSMIT_API_CLIENT_ID,
      refresh_token: authData.refresh_token,
      grant_type: 'refresh_token',
      redirect_uri: TRANSMIT_DEFAULT_REDIRECT_URI,
    })

    let responseData
    try {
      const response = await fetch(
          `${EDRINGTON_API_URL}/AuthorizationServer/token`,
          {
            method: 'POST',
            headers: {
              ...EDRINGTON_CLIENT_HEADERS,
            },
            body: requestBody,
            cache: 'no-cache',
          },
      )

      // If the renew failed, just don't return an access token
      if (!response.ok) {
        return null
      }

      responseData = await response.json()
    } catch (e) {
      throw new Error('Could not renew session')
    }

    if (responseData && responseData.access_token) {
      setSession(responseData)
      return authData.access_token
    }
  }

  // Fallback if the authData doesn't have a refresh_token value for some reason.
  return null
}
